import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { AnswerGroupTable, ColumnType, type TableColumn } from '../components/AnswerGroupTable';
import type { NewReport } from '../hooks/useNewReport';
import type { AnswerGroup } from '../types';
import { localizedText } from '../definition/types';
import { useDefinitionMetrics } from '../definition/ReportDefinitionContext';

export function MetricDetailPage({ report }: { report: NewReport }) {
  const { metricId } = useParams<{ metricId: string }>();
  const { t, i18n } = useTranslation('newReport');
  const metric = useDefinitionMetrics().find((item) => item.id === metricId);
  const title = localizedText(metric?.title, i18n.language);
  const format = metric?.presentation.format;
  const columns = useMemo(
    (): TableColumn[] => [
      { id: 'identity', type: ColumnType.LABEL, label: t('definition.teamParticipant') },
      { id: 'metric', type: ColumnType.CUSTOM_METRIC, label: title, metricId, format },
    ],
    [metricId, title, format, t],
  );
  const { selectRows, filteredData } = report;
  const rows = useMemo(() => {
    const keep = (groups: AnswerGroup[]): AnswerGroup[] =>
      groups.flatMap((group) =>
        group.customMetrics[metricId]?.value === undefined ? [] : [{ ...group, children: keep(group.children) }],
      );
    return keep(selectRows(filteredData ?? [], 'teamsAndParticipants'));
  }, [selectRows, filteredData, metricId]);
  if (!metric) return <p>{t('definition.unavailable')}</p>;
  return (
    <div style={{ maxWidth: 1000 }}>
      <AnswerGroupTable
        label={title}
        groups={rows}
        columns={columns}
        sortingEnabled
        searchEnabled
        resetKey={JSON.stringify([metricId, report.filters.answerSelections, [...report.filters.teamIds]])}
      />
    </div>
  );
}
